// frontend/src/pages/DashboardPage.tsx
import { useState, type JSX } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useDashboard } from '@/features/workspace/api/use-dashboard'
import { ModelCard, NewModelCard } from '@/features/workspace/components/ModelCard'
import { ModelsTableView } from '@/features/workspace/components/ModelsTableView'
import { FilterToolbar, type FilterState } from '@/features/workspace/components/FilterToolbar'
import { useApi } from '@/hooks/useAppApi'
import { Import, LayoutGrid, List, Plus } from 'lucide-react'
import { NewModelFlow } from '@/features/import/components/NewModelFlow'
import { cn } from '@/lib/utils'

// ============================================================
// Source type badge colors for recent imports
// ============================================================

const SOURCE_BADGE_COLORS: Record<string, string> = {
  'aft-fathom': 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  'dxf': 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  'json': 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  'revit-ifc': 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  'revit-schedule': 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300',
  'pipe-flo': 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
}


// ============================================================
// KPI Card (horizontal row above Recent Models)
// ============================================================

const KpiCard = ({ label, value, accent }: { label: string; value: number; accent?: boolean }): JSX.Element => (
  <div className="rounded-lg border bg-card p-4">
    <p className={cn('text-2xl font-bold font-mono tracking-tight', accent ? 'text-primary' : '')}>
      {value}
    </p>
    <p className="mt-0.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
      {label}
    </p>
  </div>
)

// ============================================================
// Empty State
// ============================================================

const EmptyModelGrid = ({ onNewModel }: { onNewModel: () => void }): JSX.Element => (
  <div className="col-span-2 grid grid-cols-2 gap-3">
    <button onClick={onNewModel} className="text-left">
      <Card className="border-2 border-dashed border-muted-foreground/20 transition-colors hover:border-primary/40 cursor-pointer h-full">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Plus className="h-8 w-8 text-primary/40" />
          <p className="mt-2 text-sm font-medium text-muted-foreground">
            Create a new model
          </p>
          <p className="mt-1 text-xs text-muted-foreground/60">
            Start from scratch
          </p>
        </CardContent>
      </Card>
    </button>
    <Link to="/import">
      <Card className="border-2 border-dashed border-muted-foreground/20 transition-colors hover:border-muted-foreground/40 cursor-pointer h-full">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Import className="h-8 w-8 text-muted-foreground/40" />
          <p className="mt-2 text-sm font-medium text-muted-foreground">
            Import an existing model
          </p>
          <p className="mt-1 text-xs text-muted-foreground/60">
            AFT Fathom · DXF · Revit IFC · JSON
          </p>
        </CardContent>
      </Card>
    </Link>
  </div>
)

// ============================================================
// DashboardPage
// ============================================================

// ============================================================
// (GeneratedSystemResult interface removed — was declared but never used.)

export const DashboardPage = (): JSX.Element => {
  const { user } = useApi()
  const { data: dashboard, isLoading } = useDashboard()
  const [dashboardView, setDashboardView] = useState<'cards' | 'table'>('cards')
  const [filters, setFilters] = useState<FilterState>({ search: '', projectId: null, calcStatus: null })
  const [showNewModel, setShowNewModel] = useState(false)

  const hasModels = (dashboard?.recentModels.length ?? 0) > 0

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-6">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            Welcome back, {user?.name?.split(' ')[0] || 'Engineer'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {hasModels ? "Here's what you've been working on." : 'Get started by creating or importing a piping model.'}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        </div>
      ) : (
        <div className="grid gap-6" style={{ gridTemplateColumns: '1fr 300px' }}>
          {/* LEFT: KPIs + Recent Models */}
          <div className="space-y-6">
            {/* KPI Row */}
            <div className="grid grid-cols-3 gap-3">
              <KpiCard label="Models" value={dashboard?.stats.totalModels ?? 0} accent />
              <KpiCard label="Pipes" value={dashboard?.stats.totalPipes ?? 0} />
              <KpiCard label="Converged" value={dashboard?.stats.convergedCount ?? 0} />
            </div>

            {/* Recent Models */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-[11px] font-mono font-semibold uppercase tracking-wider text-muted-foreground">
                  Recent Models
                </h2>
                <div className="flex items-center gap-2">
                  {dashboardView === 'table' && (
                    <FilterToolbar
                      filters={filters}
                      onFiltersChange={setFilters}
                      projects={dashboard?.projects ?? []}
                    />
                  )}
                  <div className="flex items-center rounded-md border">
                    <button
                      onClick={() => setDashboardView('cards')}
                      className={cn('rounded-l-md p-1.5 transition-colors', dashboardView === 'cards' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground')}
                      title="Cards view"
                    >
                      <LayoutGrid className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDashboardView('table')}
                      className={cn('rounded-r-md p-1.5 transition-colors', dashboardView === 'table' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground')}
                      title="Table view"
                    >
                      <List className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setShowNewModel(true)}>
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    New Model
                  </Button>
                  <Link to="/import">
                    <Button size="sm" variant="outline">
                      <Import className="mr-1.5 h-3.5 w-3.5" />
                      New Import
                    </Button>
                  </Link>
                </div>
              </div>

              {dashboardView === 'cards' ? (
                <div className="grid grid-cols-2 gap-3">
                  {hasModels ? (
                    <>
                      {dashboard!.recentModels.map((model): JSX.Element => (
                        <ModelCard
                          key={model.id}
                          id={model.id}
                          projectId={model.projectId}
                          projectName={model.projectName}
                          name={model.name}
                          sourceType={model.sourceType}
                          pipeCount={model.pipeCount}
                          calcStatus={model.calcStatus}
                          calcSummary={model.calcSummary}
                          updatedAt={model.updatedAt}
                        />
                      ))}
                      <NewModelCard onClick={() => setShowNewModel(true)} />
                    </>
                  ) : (
                    <EmptyModelGrid onNewModel={() => setShowNewModel(true)} />
                  )}
                </div>
              ) : (
                <ModelsTableView models={dashboard?.recentModels ?? []} filters={filters} />
              )}
            </div>
          </div>

          {/* RIGHT: Sidebar — Projects + Recent Imports */}
          <div className="space-y-6">
            {/* Projects */}
            <div>
              <h2 className="mb-3 text-[11px] font-mono font-semibold uppercase tracking-wider text-muted-foreground">
                Projects
              </h2>
              {dashboard?.projects.length ? (
                <div className="space-y-1">
                  {dashboard.projects.map((project): JSX.Element => (
                    <ProjectItem key={project.id} project={project} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No projects yet</p>
              )}
            </div>

            {/* Recent Imports */}
            <div>
              <h2 className="mb-3 text-[11px] font-mono font-semibold uppercase tracking-wider text-muted-foreground">
                Recent Imports
              </h2>
              {dashboard?.recentImports.length ? (
                <div className="space-y-1">
                  {dashboard.recentImports.map((imp): JSX.Element => {
                    const badgeColor = SOURCE_BADGE_COLORS[imp.sourceType] ?? 'bg-gray-100 text-gray-700'
                    const inner = (
                      <div className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors">
                        <Badge className={cn('text-[10px] px-1.5 py-0 shrink-0', badgeColor)}>
                          {imp.sourceType.toUpperCase().replace('-', ' ')}
                        </Badge>
                        <span className="truncate text-xs">
                          {imp.originalFileName ?? 'Unnamed file'}
                        </span>
                      </div>
                    )

                    return imp.modelId && imp.projectId ? (
                      <Link key={imp.id} to={`/projects/${imp.projectId}/models/${imp.modelId}`}>
                        {inner}
                      </Link>
                    ) : (
                      <div key={imp.id}>{inner}</div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No imports yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      <NewModelFlow open={showNewModel} onOpenChange={setShowNewModel} />
    </div>
  )
}

// ============================================================
// Expandable Project Item (sidebar)
// ============================================================

function ProjectItem({ project }: { project: { id: string; name: string; modelCount: number } }): JSX.Element {
  return (
    <Link
      to={`/projects/${project.id}`}
      className="flex items-center gap-2 rounded-md border bg-card px-3 py-2.5 transition-colors hover:bg-muted"
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-primary/10 text-xs font-semibold text-primary">
        {project.name.charAt(0).toUpperCase()}
      </div>
      <span className="flex-1 text-sm font-medium truncate">{project.name}</span>
      <span className="text-[10px] font-mono text-muted-foreground">
        {project.modelCount} model{project.modelCount !== 1 ? 's' : ''}
      </span>
    </Link>
  )
}
